import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { 
  Plus, TrendingUp, Clock, CheckCircle, BarChart3, ArrowLeft, Menu,
  AlertTriangle, Calendar, DollarSign, X, Phone, Eye, Users, Package, Search
} from 'lucide-react';
import { toast } from 'sonner';
import SideMenu from '../components/SideMenu';
import { PaymentTypeSelector, CashPaymentModal, CardPaymentModal, UPIPaymentModal } from '../components/PaymentModals';
import { BankTransferModal, OldGoldModal, MetalCreditModal } from '../components/PaymentModalsExtended';

const KittyReports = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [customers, setCustomers] = useState({});
  const [schemes, setSchemes] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'dues', 'maturities'
  const [statusFilter, setStatusFilter] = useState('Active');
  const [menuOpen, setMenuOpen] = useState(false);
  const [maturityDays, setMaturityDays] = useState(30);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Quick payment states
  const [quickPayEnrollment, setQuickPayEnrollment] = useState(null);
  const [quickPayPeriod, setQuickPayPeriod] = useState(null);
  const [showPaymentTypeSelector, setShowPaymentTypeSelector] = useState(false);
  const [activePaymentModal, setActivePaymentModal] = useState(null);

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'All' ? `?status=${statusFilter}` : '';
      const [enrollRes, custRes, schemeRes] = await Promise.all([
        axiosInstance.get(`/kitty/enrollments${params}`),
        axiosInstance.get('/customers'),
        axiosInstance.get('/kitty/schemes')
      ]);
      
      setEnrollments(enrollRes.data);
      
      // Create customer lookup
      const custMap = {};
      custRes.data.forEach(c => custMap[c.id] = c);
      setCustomers(custMap);
      
      // Create scheme lookup
      const schemeMap = {};
      schemeRes.data.forEach(s => schemeMap[s.scheme_id] = s);
      setSchemes(schemeMap);
    } catch (err) {
      toast.error('Error loading enrollments');
    } finally {
      setLoading(false);
    }
  };

  // Calculate comprehensive stats
  const stats = useMemo(() => {
    const activeEnrollments = enrollments.filter(e => e.status === 'Active');
    let overdueCount = 0;
    let nearingMaturityCount = 0;
    let totalDuesAmount = 0;
    
    activeEnrollments.forEach(enrollment => {
      // Check for overdue payments
      const hasOverdue = enrollment.schedule?.some(entry => {
        if (entry.status === 'Due' && entry.entry_type === 'CustomerPay') {
          const dueDate = new Date(entry.due_date);
          if (dueDate < new Date()) {
            totalDuesAmount += entry.expected_amount || entry.due_amount || 0;
            return true;
          }
        }
        return false;
      });
      if (hasOverdue) overdueCount++;
      
      // Check for nearing maturity
      if (enrollment.maturity_date) {
        const maturityDate = new Date(enrollment.maturity_date);
        const today = new Date();
        const daysToMaturity = Math.ceil((maturityDate - today) / (1000 * 60 * 60 * 24));
        if (daysToMaturity > 0 && daysToMaturity <= maturityDays) {
          nearingMaturityCount++;
        }
      }
    });
    
    return {
      total: enrollments.length,
      active: activeEnrollments.length,
      overdueCount,
      nearingMaturityCount,
      totalDuesAmount
    };
  }, [enrollments, maturityDays]);

  // Filter enrollments based on active tab
  const filteredEnrollments = useMemo(() => {
    let filtered = enrollments;
    
    // Apply tab filter
    if (activeTab === 'dues') {
      filtered = enrollments.filter(enrollment => {
        return enrollment.schedule?.some(entry => {
          if (entry.status === 'Due' && entry.entry_type === 'CustomerPay') {
            const dueDate = new Date(entry.due_date);
            return dueDate < new Date();
          }
          return false;
        });
      });
    } else if (activeTab === 'maturities') {
      filtered = enrollments.filter(enrollment => {
        if (enrollment.maturity_date) {
          const maturityDate = new Date(enrollment.maturity_date);
          const today = new Date();
          const daysToMaturity = Math.ceil((maturityDate - today) / (1000 * 60 * 60 * 24));
          return daysToMaturity > 0 && daysToMaturity <= maturityDays;
        }
        return false;
      });
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(enrollment => {
        const customer = customers[enrollment.customer_id];
        const scheme = schemes[enrollment.scheme_id];
        
        // Search by enrollment ID
        if (enrollment.enrollment_id?.toLowerCase().includes(query)) return true;
        
        // Search by customer name
        if (customer?.name?.toLowerCase().includes(query)) return true;
        
        // Search by customer mobile
        if (customer?.mobile?.includes(query)) return true;
        
        // Search by scheme name
        if (scheme?.scheme_name?.toLowerCase().includes(query)) return true;
        
        // Search by amount collected
        const amountCollected = enrollment.total_amount_collected?.toString() || '0';
        if (amountCollected.includes(query)) return true;
        
        return false;
      });
    }
    
    return filtered;
  }, [enrollments, activeTab, maturityDays, searchQuery, customers, schemes]);

  const getProgressPercent = (enrollment) => {
    return Math.round((enrollment.periods_paid / enrollment.total_periods) * 100);
  };

  const getNextDuePeriod = (enrollment) => {
    if (!enrollment.schedule) return null;
    return enrollment.schedule.find(entry => 
      entry.status === 'Due' && entry.entry_type === 'CustomerPay'
    );
  };

  const getDaysToMaturity = (enrollment) => {
    if (!enrollment.maturity_date) return null;
    const maturityDate = new Date(enrollment.maturity_date);
    const today = new Date();
    return Math.ceil((maturityDate - today) / (1000 * 60 * 60 * 24));
  };

  const handleQuickPay = (enrollment) => {
    const nextDue = getNextDuePeriod(enrollment);
    if (!nextDue) {
      toast.error('No pending payments found for this enrollment');
      return;
    }
    setQuickPayEnrollment(enrollment);
    setQuickPayPeriod(nextDue);
    setShowPaymentTypeSelector(true);
  };

  const handlePaymentSave = async (paymentData) => {
    if (!quickPayEnrollment || !quickPayPeriod) {
      toast.error('No period selected');
      return;
    }

    try {
      const expectedAmount = quickPayPeriod.expected_amount || quickPayPeriod.due_amount || 0;
      const paymentAmount = paymentData.amount || expectedAmount;
      
      // Check for shortfall and show warning
      if (paymentAmount < expectedAmount) {
        const shortfall = expectedAmount - paymentAmount;
        const proceed = window.confirm(
          `⚠️ PARTIAL PAYMENT WARNING\n\n` +
          `Expected Amount: ₹${expectedAmount}\n` +
          `Payment Amount: ₹${paymentAmount}\n` +
          `Shortfall: ₹${shortfall}\n\n` +
          `This period will remain "Due" until full payment is received.\n` +
          `Maturity will be blocked until all shortfalls are cleared.\n\n` +
          `Do you want to proceed with this partial payment?`
        );
        
        if (!proceed) {
          return;
        }
      }

      const paymentEntry = {
        period_index: quickPayPeriod.period_index,
        amount: paymentAmount,
        payment_type: paymentData.payment_type,
        payment_date: paymentData.date,
        reference: paymentData.reference || '',
        rate_used: paymentData.rate_used || 0,
      };

      await axiosInstance.post(
        `/kitty/enrollments/${quickPayEnrollment.enrollment_id}/pay`,
        paymentEntry
      );

      await loadData();
      
      if (paymentAmount < expectedAmount) {
        toast.warning(`Partial payment of ₹${paymentAmount} recorded. Shortfall: ₹${expectedAmount - paymentAmount}`);
      } else {
        toast.success('Payment recorded successfully');
      }
      
      setQuickPayEnrollment(null);
      setQuickPayPeriod(null);
      setActivePaymentModal(null);
      setShowPaymentTypeSelector(false);
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Kitty data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <SideMenu 
        isOpen={menuOpen} 
        onClose={() => setMenuOpen(false)}
        currentPage="kitty"
        user={user}
        onLogout={onLogout}
      />

      {/* Header - Mobile Optimized */}
      <div className="sticky top-0 z-40 bg-white shadow-md border-b-2 border-amber-300 px-3 sm:px-4 py-3">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-2">
            <button onClick={() => setMenuOpen(true)} className="p-2 hover:bg-amber-100 rounded-xl transition-colors min-w-[44px] min-h-[44px]">
              <Menu className="w-6 h-6 text-gray-700" />
            </button>
            <button 
              onClick={() => navigate(-1)} 
              className="p-2 hover:bg-amber-100 rounded-xl transition-colors min-w-[44px] min-h-[44px]"
              aria-label="Go back"
            >
              <ArrowLeft className="w-6 h-6 text-gray-700" />
            </button>
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800">Kitty Enrollments & Reports</h1>
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-4 max-w-7xl mx-auto pb-20">
        {/* Summary Cards - Standardized Amber Theme */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-4">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-3 sm:p-4 shadow-md">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs sm:text-sm text-gray-600 font-medium">Active</span>
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500 opacity-40" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-amber-700">{stats.active}</p>
          </div>
          
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-3 sm:p-4 shadow-md">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs sm:text-sm text-gray-600 font-medium">Total</span>
              <Package className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500 opacity-40" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-amber-700">{stats.total}</p>
          </div>
          
          <div className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200 rounded-xl p-3 sm:p-4 shadow-md">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs sm:text-sm text-gray-600 font-medium">Overdue</span>
              <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-red-500 opacity-40" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-red-700">{stats.overdueCount}</p>
            <p className="text-xs text-gray-500 mt-1">₹{stats.totalDuesAmount.toLocaleString('en-IN')}</p>
          </div>
          
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-3 sm:p-4 shadow-md">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs sm:text-sm text-gray-600 font-medium">Maturing</span>
              <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500 opacity-40" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-amber-700">{stats.nearingMaturityCount}</p>
            <p className="text-xs text-gray-500 mt-1">Next {maturityDays} days</p>
          </div>
        </div>

        {/* Filters - Mobile Optimized */}
        <div className="bg-white rounded-xl border-2 border-amber-200 p-2 mb-4 shadow-sm">
          <div className="flex gap-1 sm:gap-2 overflow-x-auto">
            <button
              onClick={() => setActiveTab('all')}
              className={`flex-1 py-2.5 px-2 sm:px-3 rounded-lg font-semibold text-xs sm:text-sm transition-all min-h-[44px] ${
                activeTab === 'all'
                  ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white shadow-md'
                  : 'text-gray-600 hover:bg-amber-50'
              }`}
            >
              All Enrollments
            </button>
            <button
              onClick={() => setActiveTab('dues')}
              className={`flex-1 py-2.5 px-2 sm:px-3 rounded-lg font-semibold text-xs sm:text-sm transition-all min-h-[44px] ${
                activeTab === 'dues'
                  ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-md'
                  : 'text-gray-600 hover:bg-amber-50'
              }`}
            >
              Overdue ({stats.overdueCount})
            </button>
            <button
              onClick={() => setActiveTab('maturities')}
              className={`flex-1 py-2.5 px-2 sm:px-3 rounded-lg font-semibold text-xs sm:text-sm transition-all min-h-[44px] ${
                activeTab === 'maturities'
                  ? 'bg-gradient-to-r from-orange-500 to-yellow-500 text-white shadow-md'
                  : 'text-gray-600 hover:bg-amber-50'
              }`}
            >
              Maturing ({stats.nearingMaturityCount})
            </button>
          </div>
        </div>

        {/* Filters Row - Mobile Optimized */}
        {activeTab === 'all' && (
          <div className="flex flex-wrap gap-2 mb-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2.5 border-2 border-amber-300 rounded-lg text-sm font-semibold bg-white hover:border-amber-400 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all min-h-[44px]"
            >
              <option value="Active">Active</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
              <option value="All">All Status</option>
            </select>
          </div>
        )}

        {activeTab === 'maturities' && (
          <div className="flex flex-wrap gap-2 mb-4">
            <select
              value={maturityDays}
              onChange={(e) => setMaturityDays(parseInt(e.target.value))}
              className="px-3 py-2.5 border-2 border-amber-300 rounded-lg text-sm font-semibold bg-white hover:border-amber-400 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all min-h-[44px]"
            >
              <option value="30">Next 30 Days</option>
              <option value="60">Next 60 Days</option>
              <option value="90">Next 90 Days</option>
            </select>
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by customer name, mobile, enrollment ID, scheme, or amount..."
              className="w-full pl-10 pr-4 py-3 border-2 border-amber-300 rounded-lg text-sm bg-white hover:border-amber-400 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all min-h-[44px]"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            )}
          </div>
          {searchQuery && (
            <p className="text-xs text-gray-500 mt-2">
              Found {filteredEnrollments.length} enrollment{filteredEnrollments.length !== 1 ? 's' : ''} matching "{searchQuery}"
            </p>
          )}
        </div>

        {/* Enrollment Cards */}
        {filteredEnrollments.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border-2 border-gray-200">
            <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 font-semibold">No enrollments found</p>
            <p className="text-sm text-gray-500 mt-1">
              {activeTab === 'dues' && 'All payments are up to date!'}
              {activeTab === 'maturities' && 'No enrollments maturing soon'}
              {activeTab === 'all' && 'Start by creating a new enrollment'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredEnrollments.map((enrollment) => {
              const customer = customers[enrollment.customer_id];
              const scheme = schemes[enrollment.scheme_id];
              const nextDue = getNextDuePeriod(enrollment);
              const daysToMaturity = getDaysToMaturity(enrollment);
              const isOverdue = nextDue && new Date(nextDue.due_date) < new Date();
              
              return (
                <div
                  key={enrollment.enrollment_id}
                  className={`bg-white border-2 rounded-xl p-3 sm:p-4 shadow-sm transition-all ${
                    isOverdue ? 'border-red-300 bg-red-50' : 'border-amber-200 hover:border-amber-300'
                  }`}
                >
                  {/* Header */}
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold text-amber-700 text-sm">{enrollment.enrollment_id}</p>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          enrollment.status === 'Active' 
                            ? 'bg-green-100 text-green-700'
                            : enrollment.status === 'Completed'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {enrollment.status}
                        </span>
                        {isOverdue && (
                          <span className="px-2 py-0.5 bg-red-200 text-red-800 rounded-full text-xs font-semibold flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Overdue
                          </span>
                        )}
                        {enrollment.has_shortfall && (
                          <span className="px-2 py-0.5 bg-orange-200 text-orange-800 rounded-full text-xs font-semibold flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Shortfall: ₹{enrollment.total_shortfall || 0}
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-gray-800 mt-1">{customer?.name || 'Unknown'}</p>
                      <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                        <Phone className="w-3 h-3" />
                        {customer?.mobile || 'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Scheme Info - Amber Theme */}
                  <div className="bg-amber-50 rounded-lg p-2 mb-3 border border-amber-200">
                    <p className="text-xs text-gray-600 mb-1">Scheme</p>
                    <p className="font-semibold text-sm text-amber-800">{scheme?.name || scheme?.scheme_name || 'N/A'}</p>
                    <p className="text-xs text-gray-600 mt-1">
                      ₹{(enrollment.amount_per_period || enrollment.installment_amount || 0).toLocaleString('en-IN')} × {enrollment.total_periods} periods
                    </p>
                  </div>

                  {/* Progress - Amber Gradient */}
                  <div className="mb-3">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-600">Progress</span>
                      <span className="font-semibold text-gray-800">
                        {enrollment.periods_paid}/{enrollment.total_periods} ({getProgressPercent(enrollment)}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-amber-400 to-orange-400 h-2 rounded-full transition-all"
                        style={{ width: `${getProgressPercent(enrollment)}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Next Due / Maturity Info */}
                  {nextDue && (
                    <div className="bg-amber-50 rounded-lg p-2 mb-3 border border-amber-200">
                      <p className="text-xs text-gray-600 mb-1">Next Due</p>
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-amber-800">
                          ₹{(nextDue.expected_amount || nextDue.due_amount || 0).toLocaleString('en-IN')}
                        </span>
                        <span className={`text-xs font-semibold ${
                          isOverdue ? 'text-red-600' : 'text-gray-600'
                        }`}>
                          {new Date(nextDue.due_date).toLocaleDateString('en-IN', { 
                            day: 'numeric', 
                            month: 'short', 
                            year: 'numeric' 
                          })}
                        </span>
                      </div>
                    </div>
                  )}

                  {daysToMaturity !== null && daysToMaturity <= maturityDays && (
                    <div className="bg-green-50 rounded-lg p-2 mb-3 border border-green-200">
                      <p className="text-xs text-gray-600 mb-1">Maturity</p>
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-semibold text-green-700">
                          {daysToMaturity} days remaining
                        </span>
                        <span className="text-xs text-gray-600">
                          {new Date(enrollment.maturity_date).toLocaleDateString('en-IN', { 
                            day: 'numeric', 
                            month: 'short' 
                          })}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Actions - Mobile Optimized Buttons */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => navigate(`/kitty/enrollments/${enrollment.enrollment_id}`)}
                      className="py-2.5 px-3 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-lg font-semibold hover:shadow-lg text-xs sm:text-sm flex items-center justify-center gap-1 min-h-[44px] transition-all"
                    >
                      <Eye className="w-4 h-4" />
                      View
                    </button>
                    {nextDue && enrollment.status === 'Active' && (
                      <button
                        onClick={() => handleQuickPay(enrollment)}
                        className="py-2.5 px-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-semibold hover:shadow-lg text-xs sm:text-sm flex items-center justify-center gap-1 min-h-[44px] transition-all"
                      >
                        <DollarSign className="w-4 h-4" />
                        Pay Now
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Payment Type Selector */}
      {showPaymentTypeSelector && (
        <PaymentTypeSelector
          isOpen={showPaymentTypeSelector}
          onClose={() => {
            setShowPaymentTypeSelector(false);
          }}
          onSelectType={(type) => {
            setShowPaymentTypeSelector(false);
            setActivePaymentModal(type);
          }}
        />
      )}

      {/* Payment Modals */}
      <CashPaymentModal
        isOpen={activePaymentModal === 'cash'}
        onClose={() => {
          setActivePaymentModal(null);
          setQuickPayEnrollment(null);
          setQuickPayPeriod(null);
        }}
        onSave={handlePaymentSave}
        amount={quickPayPeriod?.expected_amount || quickPayPeriod?.due_amount || 0}
      />
      <CardPaymentModal
        isOpen={activePaymentModal === 'card'}
        onClose={() => {
          setActivePaymentModal(null);
          setQuickPayEnrollment(null);
          setQuickPayPeriod(null);
        }}
        onSave={handlePaymentSave}
        amount={quickPayPeriod?.expected_amount || quickPayPeriod?.due_amount || 0}
      />
      <UPIPaymentModal
        isOpen={activePaymentModal === 'upi'}
        onClose={() => {
          setActivePaymentModal(null);
          setQuickPayEnrollment(null);
          setQuickPayPeriod(null);
        }}
        onSave={handlePaymentSave}
        amount={quickPayPeriod?.expected_amount || quickPayPeriod?.due_amount || 0}
      />
      <BankTransferModal
        isOpen={activePaymentModal === 'bank'}
        onClose={() => {
          setActivePaymentModal(null);
          setQuickPayEnrollment(null);
          setQuickPayPeriod(null);
        }}
        onSave={handlePaymentSave}
        amount={quickPayPeriod?.expected_amount || quickPayPeriod?.due_amount || 0}
      />
      <OldGoldModal
        isOpen={activePaymentModal === 'old-gold'}
        onClose={() => {
          setActivePaymentModal(null);
          setQuickPayEnrollment(null);
          setQuickPayPeriod(null);
        }}
        onSave={handlePaymentSave}
        grossWeight={quickPayPeriod?.expected_amount || quickPayPeriod?.due_amount || 0}
      />
      <MetalCreditModal
        isOpen={activePaymentModal === 'metal'}
        onClose={() => {
          setActivePaymentModal(null);
          setQuickPayEnrollment(null);
          setQuickPayPeriod(null);
        }}
        onSave={handlePaymentSave}
        requiredAmount={quickPayPeriod?.expected_amount || quickPayPeriod?.due_amount || 0}
      />
    </div>
  );
};

export default KittyReports;
